import tw from 'twin.macro'
import React, { useEffect, useRef, useState } from 'react'
import { CgInfo } from 'react-icons/cg'

import { useListenStripeProductResponseQuery, useSetStripeRequestMutation } from 'modules/ProductInfo/productInfoApi'
import { useListenPublishedSalesPageQuery, useSetCoachOrgSlugMutation } from 'modules/PageBuilder/pageBuilderApi'

import { reservedRoutes } from 'modules/ProductInfo/constants/slugConstants'
import { useFormRefsControl } from 'common/components/RefsControl/FormRefsControl/useFormRefsControl'
import { useEventListener } from 'common/hooks/useEventListener'
import { useAlert } from 'common/components/Alert/hooks/useAlert'
import { Button } from 'common/components/Button/Button'
import { Input } from 'common/components/Input/Input'
import { Tooltip } from 'common/components/Tooltip/Tooltip'
import { createUID } from 'common/utils/createUID'
import { getStandardizedName } from 'common/utils/stringUtils'
import CurrencyInput from '../CurrencyInput/CurrencyInput'
import { ShareLinkMenu } from '../ShareLinkMenu/ShareLinkMenu'
import { getStorefrontPagePath } from 'modules/ProductInfo/utils/shareLinks'

const nonAlphaNumericalRegex = /[\W_]+/g

export function ProductInfoForm({ coachOrgId, stripeProductId, stripeProductName, currency, allOrgSlugs }) {
  const { createAlert } = useAlert()
  const slugsTaken = Object.values(allOrgSlugs)
  const currOrgSlug = allOrgSlugs?.[coachOrgId] || ''
  const [currentSlug, setCurrentSlug] = useState(currOrgSlug)
  const [coachSlug, setCoachSlug] = useState(currOrgSlug)
  const [isSlugUpdating, setIsSlugUpdating] = useState(false)
  const [slugError, setSlugError] = useState(null)
  const [nameError, setNameError] = useState(null)
  const [productName, setProductName] = useState(stripeProductName)
  const [requestId, setRequestId] = useState(null)

  const [setStripeRequest] = useSetStripeRequestMutation()
  const [setCoachOrgSlug] = useSetCoachOrgSlugMutation()

  const { data: salesPageData } = useListenPublishedSalesPageQuery({ coachOrgId })
  const salesPageLoading = salesPageData === undefined || salesPageData?.isLoading
  const membershipPagePath = salesPageLoading ? '' : getStorefrontPagePath({ salesPage: salesPageData })

  const { data: stripeResponse } = useListenStripeProductResponseQuery({ coachOrgId })
  const stripeResponsePending = stripeResponse?.requestKey === requestId && stripeResponse?.status === 'pending'
  const stripeResponseSuccess = stripeResponse?.requestKey === requestId && stripeResponse?.status === 'success'
  const stripeResponseError = stripeResponse?.requestKey === requestId && stripeResponse?.status === 'error'

  const isUpdateDisabled =
    Boolean(nameError) || Boolean(slugError) || (productName === stripeProductName && coachSlug === currOrgSlug)
  const isUpdating = requestId || stripeResponsePending || isSlugUpdating

  const updateSlug = async () => {
    const stdCoachSlug = getStandardizedName(coachSlug)
    const isSlugTaken = slugsTaken.find((slug) => getStandardizedName(slug) === getStandardizedName(stdCoachSlug))
    if (stdCoachSlug !== currOrgSlug) {
      if (reservedRoutes[stdCoachSlug] || isSlugTaken) {
        setSlugError('This handle is taken, please choose a different handle.')
        return { success: false }
      } else {
        setIsSlugUpdating(true)
        await setCoachOrgSlug({ coachOrgId, slug: stdCoachSlug })
        setCurrentSlug(stdCoachSlug)
        setIsSlugUpdating(false)
        return { success: true }
      }
    } else {
      return { success: true }
    }
  }

  const handleUpdate = async () => {
    const { success } = await updateSlug()
    if (success) {
      const uid = createUID()
      setRequestId(uid)
      await setStripeRequest({
        requestId: uid,
        coachOrgId,
        type: 'UPDATE_PRODUCT',
        payload: {
          stripeProductId,
          name: productName,
        },
      })
    }
  }

  useEffect(() => {
    setProductName(stripeProductName)
  }, [stripeProductName])

  useEffect(() => {
    if (stripeResponseError) {
      setRequestId(null)
      const errorMsg = stripeResponse?.errorMsg || 'Update failed!'
      createAlert({ text: errorMsg, type: 'danger' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeResponseError])

  useEffect(() => {
    if (stripeResponseSuccess) {
      setRequestId(null)
      createAlert({ text: 'Updated!', type: 'success' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeResponseSuccess])

  const { addInputRef, moveFocusOnKeyPress } = useFormRefsControl()
  const productNameRef = useRef()
  const coachSlugRef = useRef()
  const currencyInputRef = useRef()
  const updateBtnRef = useRef()
  const inputRefsSortMethod = ['productName', 'coachSlug', 'currency', 'updateBtn']
  useEffect(() => {
    addInputRef({ ref: productNameRef, name: 'productName', sortMethod: inputRefsSortMethod })
    addInputRef({ ref: coachSlugRef, name: 'coachSlug', sortMethod: inputRefsSortMethod })
    addInputRef({ ref: currencyInputRef, name: 'currency', sortMethod: inputRefsSortMethod })
    addInputRef({
      ref: updateBtnRef,
      name: 'updateBtn',
      sortMethod: inputRefsSortMethod,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEventListener('keydown', moveFocusOnKeyPress)

  return (
    <>
      <div className='mb-4'>
        <div className='flex items-center mb-1'>
          <label htmlFor='productName' className='font-semibold text-tBlack cursor-pointer mr-1'>
            Product name
          </label>
          <Tooltip
            content='The product name will be shown to users when checking out through Stripe'
            triggerClasses='cursor-pointer text-gray-500 hover:text-tGreen'
          >
            <CgInfo className='w-4 h-4' />
          </Tooltip>
        </div>
        <Input
          inputRef={productNameRef}
          name='productName'
          placeholder='Enter your product name'
          value={productName}
          onChange={(e) => {
            if (nameError) {
              setNameError(null)
            }

            if (!e?.target?.value?.length) {
              setNameError('Product name is required')
            }

            setProductName(e.target.value)
          }}
          error={Boolean(nameError)}
          disabled={isUpdating}
          autoFocus={true}
        />
        {Boolean(nameError) && <div className='text-xs mt-1 text-tRed'>{nameError}</div>}
      </div>
      <div className='mb-4 flex flex-col items-start'>
        <div className='flex items-center mb-1'>
          <label htmlFor='productName' className='font-semibold text-tBlack cursor-pointer mr-1'>
            Your link
          </label>
          <Tooltip
            content='This will set your page links. You can edit this later, but avoid changing too often as it can negatively affect your page SEO.'
            triggerClasses='cursor-pointer text-gray-500 hover:text-tGreen'
          >
            <CgInfo className='w-4 h-4' />
          </Tooltip>
        </div>
        <Input
          inputRef={coachSlugRef}
          name='coachSlug'
          placeholder='Enter your link handle'
          value={coachSlug}
          onChange={(e) => {
            if (slugError) {
              setSlugError(null)
            }
            const value = e.target.value.replace(nonAlphaNumericalRegex, '-').toLowerCase()
            setCoachSlug(value)
            if (!value?.length) {
              setSlugError('Link handle is required')
            }
          }}
          disabled={isUpdating}
          error={Boolean(slugError)}
          maxLength={40}
        />
        {Boolean(slugError) && <div className='text-xs mt-1 text-tRed'>{slugError}</div>}
        <ShareLinkMenu
          coachSlug={currentSlug || 'your-slug'}
          pagePath={membershipPagePath}
          className='mt-2 bg-gray-100 rounded py-1 px-1.5 text-xs flex items-center max-w-[100%] cursor-pointer'
        >
          <span className='truncate'>
            https://my.trybe.fit/
            <span className='font-bold truncate'>{currentSlug || 'your-slug'}</span>
          </span>
        </ShareLinkMenu>
      </div>
      <div className='mb-6'>
        <CurrencyInput
          inputRef={currencyInputRef}
          selectedCurrency={currency}
          disabled={true}
          tooltipText='To change to another currency, please contact support@trybe.do'
        />
      </div>
      <Button
        ref={updateBtnRef}
        onClick={handleUpdate}
        disabled={isUpdateDisabled}
        loading={isUpdating}
        css={tw`ml-auto`}
      >
        Update
      </Button>
    </>
  )
}
